// ===============================
// IMPORTS
// ===============================
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useSearchParams, Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Icons
import {
  MapPin,
  Layers,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";

// API and Utils
import api from "../../../../../utils/api";
import { createCustomIcon, validateCoordinates, BASE_LAYERS } from "./utils";

// Components
import { Button, useToast } from "../../components/AdminSharedComponents";
import { SearchInput } from "../../components/ui/SearchInput";
import { DropdownFilter } from "../../components/ui/DropdownFilter";
import { ResetButton } from "../../components/ui/ResetButton";
import ProjectCard from "./ProjectCard";
import ProjectDetail from "./ProjectDetail";
import MapControls from "./MapControls";
import StatisticsCards from "./StatisticsCards";
import Legend from "./Legend";

// ===============================
// CONSTANTS
// ===============================
const ITEMS_PER_PAGE = 3;
// ===============================
// INTERNAL COMPONENTS
// ===============================

// Component to handle map interactions
const MapController = ({ selectedProject, excavationPoints }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedProject && map) {
      const validCoords = validateCoordinates(selectedProject.coordinates);

      if (validCoords) {
        if (excavationPoints && excavationPoints.length > 0) {
          // Create markers for bounds calculation
          const markers = excavationPoints.map((point) =>
            L.marker([point.coordinates.lat, point.coordinates.lng])
          );

          if (markers.length > 0) {
            const bounds = L.featureGroup(markers).getBounds().pad(0.1);
            map.flyToBounds(bounds, {
              duration: 1.5,
              easeLinearity: 0.1,
            });
          }
        } else {
          // No excavation points, fly to project location
          map.flyTo([validCoords.lat, validCoords.lng], MAP_ZOOM_PROJECT, {
            duration: 1.5,
            easeLinearity: 0.1,
          });
        }
      }
    }
  }, [selectedProject, excavationPoints, map]);

  return null;
};

const DEFAULT_MAP_CENTER = [-2.5489, 118.0149];
const DEFAULT_MAP_ZOOM = 5;
const MAP_ZOOM_PROJECT = 18;

const ProjectMap = () => {
  const mapInstanceRef = useRef(null);
  const excavationMarkersRef = useRef([]);
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");

  // State
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [excavationPoints, setExcavationPoints] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hoveredButton, setHoveredButton] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBaseLayer, setSelectedBaseLayer] = useState(BASE_LAYERS[0]);
  const [isInitialized, setIsInitialized] = useState(false);

  // ===============================
  // COMPUTED VALUES
  // ===============================

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter projects using useMemo instead of useEffect
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    // Apply search filter
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.location?.toLowerCase().includes(term) ||
          p.client_name?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((p) => {
        const normalizedStatus = p.status?.toLowerCase().replace(/\s+/g, "-");
        const normalizedFilter = statusFilter.toLowerCase();
        return (
          normalizedStatus === normalizedFilter || p.status === statusFilter
        );
      });
    }

    return filtered;
  }, [projects, debouncedSearchTerm, statusFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const currentProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProjects, currentPage]);

  const hasFilters = useMemo(
    () => searchTerm || statusFilter !== "all",
    [searchTerm, statusFilter]
  );

  const statusOptions = useMemo(
    () => [
      { value: "all", label: "All Status" },
      { value: "completed", label: "Completed" },
      { value: "in-progress", label: "In Progress" },
      { value: "pending", label: "On Hold" },
      { value: "cancelled", label: "Cancelled" },
    ],
    []
  );

  const statusCounts = useMemo(
    () => ({
      total: projects.length,
      completed: projects.filter(
        (p) => p.status === "completed" || p.status === "Completed"
      ).length,
      inProgress: projects.filter(
        (p) => p.status === "in-progress" || p.status === "In Progress"
      ).length,
      onHold: projects.filter(
        (p) => p.status === "pending" || p.status === "On Hold"
      ).length,
      cancelled: projects.filter(
        (p) => p.status === "cancelled" || p.status === "Cancelled"
      ).length,
    }),
    [projects]
  );

  // ===============================
  // UTILITY FUNCTIONS
  // ===============================
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const clearExcavationMarkers = useCallback(() => {
    excavationMarkersRef.current.forEach((marker) => {
      try {
        mapInstanceRef.current?.removeLayer(marker);
      } catch (error) {
        // Ignore removal errors - marker might already be removed
        console.debug("Marker removal error (ignored):", error);
      }
    });
    excavationMarkersRef.current = [];
  }, []);

  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter("all");
    setSelectedProject(null);
    setCurrentPage(1);
    clearExcavationMarkers();
    setIsInitialized(false);
  }, [clearExcavationMarkers]);

  const resetMapView = useCallback(() => {
    mapInstanceRef.current?.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
    clearExcavationMarkers();
  }, [clearExcavationMarkers]);

  const handleBackToList = useCallback(() => {
    setSelectedProject(null);
    setExcavationPoints([]);
    clearExcavationMarkers();
    // Reset map view to default
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
    }
  }, [clearExcavationMarkers]);

  const handleLayerChange = useCallback((layerName) => {
    const layer = BASE_LAYERS.find((l) => l.name === layerName);
    if (layer) {
      setSelectedBaseLayer(layer);
    }
  }, []);

  // ===============================
  // MAIN FUNCTIONS
  // ===============================

  const selectProject = useCallback(
    async (project) => {
      setSelectedProject(project);
      clearExcavationMarkers();

      try {
        const res = await api.get(
          `/api/excavation-points?project_id=${project.id}`
        );
        const points = res.data?.data || res.data || [];
        setExcavationPoints(points);

        // The MapController component will handle the fly to animation
      } catch (error) {
        console.error("Failed to load excavation points:", error);
        toast.error("Failed to load excavation points");
        setExcavationPoints([]);
      }
    },
    [clearExcavationMarkers, toast]
  );

  // ===============================
  // EFFECTS
  // ===============================
  useEffect(() => {
    const controller = new AbortController();

    const fetchProjects = async () => {
      try {
        const res = await api.get("/api/projects/locations", {
          signal: controller.signal,
        });
        setProjects(res.data);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error fetching project locations:", error);
          toast.error("Failed to load project locations");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
    return () => controller.abort();
  }, []);

  // Handle projectId from URL parameter
  useEffect(() => {
    if (projects.length > 0 && projectId && !isInitialized) {
      const target = projects.find((p) => String(p.id) === projectId);
      if (target) {
        selectProject(target);
        setIsInitialized(true);
      }
    }
  }, [projects, projectId, isInitialized, selectProject]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-pulse text-primary">Loading map...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary-dark flex items-center gap-2">
              <MapPin className="h-6 w-6" />
              Project Locations Map
            </h1>
            <p className="text-gray-600 mt-1">
              View all project locations and their current status
            </p>
          </div>
          <StatisticsCards statusCounts={statusCounts} />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-120px)]">
        {/* Sidebar */}
        <div
          className={`bg-white border-r border-gray-200 transition-all duration-300 ${
            showSidebar ? "w-full lg:w-96" : "w-0 overflow-hidden"
          }`}
        >
          <div className="h-full flex flex-col">
            {!selectedProject ? (
              <>
                <div className="p-5 border-b border-gray-200">
                  <div className="space-y-4">
                    <SearchInput
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder="Search projects, locations..."
                    />
                    <div className="flex gap-3">
                      <DropdownFilter
                        options={statusOptions}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        className="flex-grow"
                      />
                      <ResetButton
                        onClick={resetFilters}
                        disabled={!hasFilters}
                        className={
                          hasFilters
                            ? "w-28"
                            : "opacity-50 cursor-not-allowed w-28"
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-primary-dark">
                    Project Locations ({filteredProjects.length})
                  </h2>
                  <div className="flex items-center gap-2">
                    <Link to="/admin/projects/add" className="hidden lg:block">
                      <Button
                        variant="primary"
                        className="flex items-center gap-2 text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add New</span>
                      </Button>
                    </Link>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowSidebar(false)}
                      className="lg:hidden"
                    >
                      <Layers className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {currentProjects.length > 0 ? (
                    currentProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        onSelect={selectProject}
                        formatDate={formatDate}
                      />
                    ))
                  ) : (
                    <div className="text-center">
                      <MapPin className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-primary-dark mb-2">
                        No Projects Found
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {hasFilters
                          ? "Try adjusting your search or filter criteria."
                          : "No projects with location data available."}
                      </p>
                    </div>
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <ProjectDetail
                project={selectedProject}
                onBack={handleBackToList}
                formatDate={formatDate}
              />
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative h-[calc(100vh-120px)]">
          <MapControls
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            hoveredButton={hoveredButton}
            setHoveredButton={setHoveredButton}
            resetMapView={resetMapView}
            selectedBaseLayer={selectedBaseLayer}
            onLayerChange={handleLayerChange}
          />
          <Legend />

          <MapContainer
            center={DEFAULT_MAP_CENTER}
            zoom={DEFAULT_MAP_ZOOM}
            style={{ height: "100%", width: "100%" }}
            whenCreated={(map) => (mapInstanceRef.current = map)}
          >
            <MapController
              selectedProject={selectedProject}
              excavationPoints={excavationPoints}
            />
            <TileLayer
              url={selectedBaseLayer.url}
              attribution={selectedBaseLayer.attribution}
            />

            {filteredProjects.map((project) => {
              const coords = validateCoordinates(project.coordinates);
              if (!coords) return null;

              const handleMarkerClick = () => {
                selectProject(project);
                setShowSidebar(true);
              };

              return (
                <Marker
                  key={project.id}
                  position={[coords.lat, coords.lng]}
                  icon={createCustomIcon(project.status)}
                  eventHandlers={{ click: handleMarkerClick }}
                >
                  <Popup
                    className="custom-popup"
                    closeButton={false}
                    autoPan={true}
                    keepInView={true}
                  >
                    <div className="min-w-52 sm:min-w-64">
                      <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                        {project.name}
                      </h3>
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium">{project.status}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Location:</span>
                          <span className="font-medium">
                            {project.location || "N/A"}
                          </span>
                        </div>
                        <div className="pt-2 border-t text-gray-500 text-xs">
                          Lat: {coords.lat.toFixed(5)}, Long:{" "}
                          {coords.lng.toFixed(5)}
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      </div>

      {/* Mobile toggle button */}
      <div className="lg:hidden fixed bottom-4 right-4 z-[1001]">
        <Button
          onClick={() => setShowSidebar(!showSidebar)}
          className="shadow-lg"
        >
          <BarChart3 className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default ProjectMap;
